import { createLocationIcon } from '../components/MapIcons';
import { useToast } from '@/hooks/use-toast';
import L from 'leaflet';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMap } from 'react-leaflet';

export const useLocationMarker = () => {
    const [position, setPosition] = useState<L.LatLng | null>(null);
    const [accuracy, setAccuracy] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const map = useMap();
    const { toast } = useToast();
    const { t } = useTranslation();
    const markerRef = useRef<L.Marker | null>(null);
    const circleRef = useRef<L.Circle | null>(null);

    const handleLocate = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        setLoading(true);
        map.locate({ setView: true, maxZoom: 16 });
    }, [map]);

    useEffect(() => {
        const onLocationFound = (e: L.LocationEvent) => {
            setPosition(e.latlng);
            setAccuracy(e.accuracy);
            setLoading(false);

            if (markerRef.current) {
                markerRef.current.remove();
            }
            if (circleRef.current) {
                circleRef.current.remove();
            }

            const locationIcon = createLocationIcon();
            markerRef.current = L.marker(e.latlng, { icon: locationIcon })
                .addTo(map)
                .bindPopup(`
                    <div class="text-sm font-medium">
                        ${t('properties.location.yourLocation')}
                    </div>
                    <div class="text-xs text-gray-500 mt-1">
                        ${t('properties.location.accuracy')}: ${Math.round(e.accuracy)} ${t('properties.location.meters')}
                    </div>
                    <div class="text-xs mt-1">
                        ${e.latlng.lat.toFixed(6)}, ${e.latlng.lng.toFixed(6)}
                    </div>
                `);

            circleRef.current = L.circle(e.latlng, {
                radius: e.accuracy,
                color: '#3b82f6',
                fillColor: '#3b82f6',
                fillOpacity: 0.1,
                weight: 1
            }).addTo(map);

            toast({
                title: t('properties.location.locationFound'),
                description: t('properties.location.locationFoundDesc'),
            });
        };

        const onLocationError = (e: L.ErrorEvent) => {
            setLoading(false);
            toast({
                title: t('properties.location.locationError'),
                description: e.message,
                variant: "destructive",
            });
        };

        map.on('locationfound', onLocationFound);
        map.on('locationerror', onLocationError);

        return () => {
            map.off('locationfound', onLocationFound);
            map.off('locationerror', onLocationError);
        };
    }, [map, t, toast]);

    return {
        loading,
        handleLocate
    };
};